import axiosInstance from "./axiosConfig";
// Removed Stripe utility imports as initialization is handled in useCheckout
// and element processing is handled in PaymentStep component.
// import { initializeStripe, initializeStripeElements } from "../utils/stripe";

/**
 * Service for handling checkout operations via backend API
 */
const checkoutService = {
  /**
   * Create a checkout session via the backend API.
   * The backend will handle creating the Stripe Payment Intent if needed.
   * @param {Object} checkoutData - Data required for checkout (shipping, billing, payment method, etc.)
   * @returns {Promise<Object>} Checkout session data from the backend (including clientSecret if applicable)
   */
  createCheckoutSession: async (checkoutData) => {
    try {
      console.log(
        "checkoutService: Sending create session request to /api/v1/checkout/session",
        checkoutData
      );
      const response = await axiosInstance.post(
        `/checkout/session`,
        checkoutData
      );
      console.log(
        "checkoutService: Create session response received",
        response.data
      );
      // The response should contain { orderId, orderNumber, clientSecret?, paymentMethod, total, status }
      return response.data;
    } catch (error) {
      console.error(
        "checkoutService: Error creating checkout session:",
        error.response || error
      );
      // Rethrow a consistent error object
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Error creating checkout session"
      );
    }
  },

  /**
   * Confirm payment completion with the backend.
   * Used after Stripe confirmation (via webhook or client-side) or for manual methods.
   * @param {Object} paymentData - Payment confirmation data
   * @param {string} paymentData.orderId - Order ID
   * @param {string} [paymentData.paymentIntentId] - Stripe payment intent ID (for Stripe payments)
   * @param {string} [paymentData.transactionId] - Transaction ID (for manual methods like bank transfer)
   * @returns {Promise<Object>} Backend confirmation result (e.g., { success, orderId, status })
   */
  confirmPayment: async (paymentData) => {
    try {
      console.log(
        "checkoutService: Sending confirm payment request to /api/v1/checkout/confirm-payment",
        paymentData
      );
      const response = await axiosInstance.post(
        `/checkout/confirm-payment`,
        paymentData
      );
      console.log(
        "checkoutService: Confirm payment response received",
        response.data
      );
      // The response should contain { success, orderId, orderNumber, status, paymentStatus }
      return response.data;
    } catch (error) {
      console.error(
        "checkoutService: Error confirming payment:",
        error.response || error
      );

      // Improved error handling with specific messages for Twint payments
      if (error.response?.data?.paymentMethod === "twint") {
        throw new Error(
          error.response?.data?.message ||
            "TWINT payment processing error. Please check your TWINT app for notifications."
        );
      }

      // Rethrow a consistent error object
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Error confirming payment"
      );
    }
  },

  /**
   * Get order details using a Stripe Payment Intent ID.
   * Used for handling redirects where only the Payment Intent is known.
   * @param {string} paymentIntentId - The Stripe Payment Intent ID.
   * @returns {Promise<Object>} Order details ({ success, orderId, orderNumber, status, paymentStatus })
   */
  getOrderByPaymentIntent: async (paymentIntentId) => {
    try {
      console.log(
        `checkoutService: Sending request to get order by PI: ${paymentIntentId}`
      );
      const response = await axiosInstance.get(
        `/checkout/order-by-intent/${paymentIntentId}`
      );
      console.log(
        "checkoutService: Get order by PI response received",
        response.data
      );
      return response.data;
    } catch (error) {
      console.error(
        `checkoutService: Error fetching order by PI ${paymentIntentId}:`,
        error.response || error
      );
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Error fetching order details by Payment Intent"
      );
    }
  },

  // Removed frontend Stripe initialization/processing methods
  // initializeStripe: async () => { ... },
  // initializeStripeElements: (stripe, clientSecret) => { ... },
  // processStripePayment: async (paymentData) => { ... },
};

export default checkoutService;
